import { brandApi } from '@/api/account-management/brandApi';
import { organizationApi } from '@/api/account-management/organizationApi';
import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';
import { type Organization } from '@/models/account-management/Organization';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useStytchOrganization } from '@stytch/react/b2b';
import { useEffect, useMemo } from 'react';
import { useAppContext } from '../../AppContext';
import { useUnsavedChanges } from '../../hooks/unsavedChangesContext';
import { useAlert } from '../common/alert/AlertProvider';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { assignedBrands, assignedLocations } from '../../permissions/locationScope';
import { useAssignment } from '../../permissions/useLocationScope';

// Global Organization -> Brand -> Location scope picker in the top bar.
// The VIEWED org is organizationId — dropdown-driven for super-admins, pinned to the authenticated org for
// everyone else. The URL slug is stable login identity and is NOT touched by switching orgs. Only TouchTab
// super-admins get the Org dropdown; every other user sees just the Brand + Location pickers.
const ScopeSelector = () => {
    const app = useAppContext();
    const theme = useTheme();
    const { isDirty, discard } = useUnsavedChanges();
    const alert = useAlert();
    const strings = useLocalizedStrings();

    // Changing scope is not a route change, so the router's blocker never sees it and an in-progress edit was
    // being thrown away silently. Ask the same question the router asks, and only apply the change on "Leave".
    //
    // Answering Leave also has to ACT on the answer. It used to change the scope and stop there, which left a
    // brand or location editor open, still dirty, still showing a record from the scope the user had just
    // left. discard() tells each guarded screen to go (TTD-149).
    // Switching scope takes you off whatever you were looking at, so the editor closes either way. Unsaved
    // edits get asked about first; with nothing pending there is nothing to ask, but the editor still has to
    // go — it was showing a record belonging to the scope just left, which is misleading whether or not
    // anything had been typed into it.
    const confirmLeave = async () => {
        if (isDirty) {
            const leave = await alert({
                title: strings.unsavedChangesTitle,
                message: strings.unsavedChangesMessage,
                agreeButtonTitle: strings.unsavedChangesLeave,
                closeButtonTitle: strings.unsavedChangesStay,
                destructive: true,
            });

            if (!leave) return false;
        }

        discard();
        return true;
    };

    // Identity comes from the Stytch session. Only super-admins can switch orgs, so only they need the org
    // list — a single-org user is pinned to the org they authenticated into and never calls /organizations.
    const superAdmin = app.isSuperAdmin();
    const { organization } = useStytchOrganization();

    const { data: fetchedOrgs, isSuccess: orgsLoaded } = useQuery<Organization[]>({
        queryKey: ['organizations'],
        queryFn: organizationApi.list,
        enabled: superAdmin,
        staleTime: Infinity,
    });

    // The fetched list is customer orgs only (the super-admin's own org — TouchTab — is excluded server-side).
    // Add it back as a selectable option so a super-admin can manage TouchTab itself (users/roles/config).
    const orgs = useMemo<Organization[]>(() => {
        const list = fetchedOrgs ?? [];
        if (!superAdmin || !organization) return list;
        const authOrg = {
            id: organization.organization_id,
            name: organization.organization_name,
            slug: organization.organization_slug,
        } as Organization;
        return list.some((o) => o.id === authOrg.id) ? list : [authOrg, ...list];
    }, [fetchedOrgs, superAdmin, organization]);
    // Brands arrive WITH their locations, because "which brands can this user reach?" cannot be answered
    // without them. The key is a suffix of the plain ['brands', orgId] key so the existing brand mutations,
    // which invalidate that prefix, still reach this list (TTD-160).
    const { data: orgBrands, isLoading: brandsFetching } = useQuery<Brand[]>({
        queryKey: ['brands', app.organizationId, 'with-locations'],
        enabled: !!app.organizationId,
        queryFn: () => brandApi.listByOrganization(app.organizationId!, true),
        staleTime: Infinity,
    });
    const { data: brandLocations, isLoading: locationsFetching } = useQuery<Location[]>({
        queryKey: ['locations', app.brandId ?? ''],
        enabled: !!app.brandId,
        queryFn: () => brandApi.listLocations(app.brandId!),
        staleTime: Infinity,
    });

    // Restrict the pickers to the user's assigned locations. The rule (super-admin exemption, absent field
    // vs empty array, undecided-counts-as-restricted) lives in useAssignment, which every screen that has to
    // scope a location list now shares — QA found four that were still showing the brand's whole estate
    // because this logic was private to the picker (TTD-160).
    const { assigned, restrict } = useAssignment();

    const brands = restrict ? assignedBrands(orgBrands, assigned) : orgBrands;
    const locations = restrict ? assignedLocations(brandLocations, assigned) : brandLocations;

    // The reconciliation effects below only act on a list that has LOADED — an in-flight list must not look
    // like a list the current selection has dropped out of, or a saved brand/location would be cleared on
    // every sign-in. The filtered lists are undefined until the assignment is known, so the loading flags
    // have to stay true that long as well.
    const brandsLoading = brandsFetching || (restrict && !assigned);
    const locationsLoading = locationsFetching || (restrict && !assigned);

    // Reconcile the VIEWED org (organizationId).
    //  - Super-admin: the picker owns it and it's persisted (restores the last-viewed org across refreshes).
    //    Only drop a genuinely stale selection — an org missing from the list AFTER it has loaded. The customer
    //    org list loads async, and until then `orgs` holds just the authenticated org (seeded by the memo), so
    //    gating on orgsLoaded is essential: without it, a super-admin viewing a customer org has their whole
    //    scope wiped on every refresh / re-login (the persisted org looks "stale" against the not-yet-loaded
    //    list). isSuccess (not isFetched) so a transient list-fetch error never clears the selection either.
    //  - Everyone else: pin it to the org they authenticated into (from the Stytch session), so neither a
    //    tampered URL nor stale local state can move them off their own org.
    useEffect(() => {
        if (superAdmin) {
            if (orgsLoaded && app.organizationId && !orgs.some((o) => o.id === app.organizationId)) {
                app.setViewedOrganizationId(null);
            }
        } else {
            const authOrgId = organization?.organization_id;
            if (authOrgId && authOrgId !== app.organizationId) app.setViewedOrganizationId(authOrgId);
        }
    }, [superAdmin, orgs, orgsLoaded, organization, app.organizationId, app]);

    // Keep the slug (login identity) in lockstep with the authenticated org on EVERY load — not just at
    // Login. A restored Stytch session never re-renders Login, so without this a stale slug in localStorage
    // (e.g. left by an older build) would linger and drive links to the wrong org. Identity only; the viewed
    // org (organizationId) is untouched.
    useEffect(() => {
        const authSlug = organization?.organization_slug;
        if (authSlug && authSlug !== app.organizationSlug) app.setOrganizationSlug(authSlug);
    }, [organization, app.organizationSlug, app]);

    // Switching the org changes only the VIEWED org (+ resets brand/location) — the URL slug stays put.
    const handleOrgChange = async (orgId: string) => {
        if (!orgId || orgId === app.organizationId) return;
        if (!(await confirmLeave())) return;
        app.setViewedOrganizationId(orgId);
    };

    const handleBrandChange = async (brandId: string | null) => {
        if (brandId === (app.brandId ?? null)) return;
        if (!(await confirmLeave())) return;
        app.setBrandId(brandId);
    };

    const handleLocationChange = async (locationId: string | null) => {
        if (locationId === (app.locationId ?? null)) return;
        if (!(await confirmLeave())) return;
        app.setLocationId(locationId);
    };

    // A single brand or location is not a choice — selecting it by hand is busywork on every sign-in for
    // single-brand customers. Only auto-select once the list has actually loaded and nothing is chosen yet,
    // so this never overrides a real selection or fires against an empty in-flight list.
    useEffect(() => {
        if (!app.organizationId || app.brandId) return;
        if (brandsLoading || brands?.length !== 1) return;
        app.setBrandId(brands[0].id ?? null);
    }, [app, app.organizationId, app.brandId, brands, brandsLoading]);

    useEffect(() => {
        if (!app.brandId || app.locationId) return;
        if (locationsLoading || locations?.length !== 1) return;
        app.setLocationId(locations[0].id ?? null);
    }, [app, app.brandId, app.locationId, locations, locationsLoading]);

    // A selection is persisted, so one that has since been deleted came back as a blank picker that could not
    // be cleared. Drop it once the list has loaded and does not contain it — same "only when genuinely stale"
    // rule the viewed-org reconciliation above uses.
    useEffect(() => {
        if (brandsLoading || !brands || !app.brandId) return;
        if (!brands.some((b) => b.id === app.brandId)) app.setBrandId(null);
    }, [app, app.brandId, brands, brandsLoading]);

    useEffect(() => {
        if (locationsLoading || !locations || !app.locationId) return;
        if (!locations.some((l) => l.id === app.locationId)) app.setLocationId(null);
    }, [app, app.locationId, locations, locationsLoading]);

    // Non-super-admins are locked to their authenticated org — no switcher, regardless of what the org list
    // returns. Super-admins still only need it when there's more than one org to switch between.
    const showOrgPicker = superAdmin && (orgs?.length ?? 0) > 1;

    // The TouchTab org owns no brands or locations, so these pickers are permanently empty there. Showing two
    // dead controls implies something is selectable when nothing is.
    const showBrandAndLocation = !app.isViewingSuperAdminOrg();

    const selectSx = {
        color: theme.palette.primary.contrastText,
        '& .MuiSelect-select': { fontWeight: 600 },
        '& .MuiOutlinedInput-notchedOutline': { border: 0 },
        '& .MuiSelect-icon': { color: theme.palette.primary.contrastText },
    };

    const menuProps = {
        anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
        transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
    };

    const spinner = (loading: boolean) =>
        loading ? <CircularProgress size={16} sx={{ mr: 2, color: theme.palette.primary.contrastText }} /> : null;

    return (
        <>
            {showOrgPicker && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        inputProps={{ 'aria-label': 'organization' }}
                        displayEmpty
                        value={app.organizationId ?? ''}
                        onChange={(e) => handleOrgChange(e.target.value as string)}
                        sx={selectSx}
                        MenuProps={menuProps}
                        renderValue={(selected) =>
                            selected ? (orgs?.find((o) => o.id === selected)?.name ?? '') : <span style={{ opacity: 0.6 }}>Select organization</span>
                        }
                    >
                        {orgs?.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                    </Select>
                </FormControl>
            )}

            {showBrandAndLocation && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                    inputProps={{ 'aria-label': 'brand' }}
                    displayEmpty
                    disabled={!app.organizationId}
                    value={app.brandId ?? ''}
                    onChange={(e) => void handleBrandChange((e.target.value as string) || null)}
                    sx={selectSx}
                    MenuProps={menuProps}
                    renderValue={(selected) =>
                        selected ? (brands?.find((b) => b.id === selected)?.name ?? '') : <span style={{ opacity: 0.6 }}>Select brand</span>
                    }
                    endAdornment={app.organizationId ? spinner(brandsLoading) : null}
                >
                    {brands?.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </Select>
            </FormControl>
            )}

            {showBrandAndLocation && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                    inputProps={{ 'aria-label': 'location' }}
                    displayEmpty
                    disabled={!app.brandId}
                    value={app.locationId ?? ''}
                    onChange={(e) => void handleLocationChange((e.target.value as string) || null)}
                    sx={selectSx}
                    MenuProps={menuProps}
                    renderValue={(selected) =>
                        selected ? (locations?.find((l) => l.id === selected)?.name ?? '') : <span style={{ opacity: 0.6 }}>Select location</span>
                    }
                    endAdornment={app.brandId ? spinner(locationsLoading) : null}
                >
                    {locations?.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
            </FormControl>
            )}
        </>
    );
};

export default ScopeSelector;
